import React, { useEffect, useRef } from 'react';
import styles from './DoodleLoader.module.css';

const DoodleLoader = ({ message = "" }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const size = 520;
    canvas.width = size;
    canvas.height = size;

    let time = 0;
    let animationId = null;

    // Helper: organic wobbly offsets (hand-drawn illusion)
    function jitter(seedOffset, intensity = 1.2) {
      return (Math.sin(time * 5.4 + seedOffset) * 0.9 + Math.cos(time * 4.7 + seedOffset * 1.3) * 0.7) * intensity;
    }

    // Wobbly multi-stroke circle with sketchy vibe
    function sketchyCircle(cx, cy, radius, lineWidth, baseColor, wobbleFactor = 1.8, dashed = false) {
      ctx.save();
      ctx.beginPath();
      const steps = 52;
      for (let i = 0; i <= steps; i++) {
        const angle = (i / steps) * Math.PI * 2;
        let noise = Math.sin(angle * 6 + time * 6) * 1.1 + Math.cos(angle * 4 - time * 5) * 0.9;
        noise += Math.sin(angle * 12) * 0.5;
        const r = radius + noise * wobbleFactor;
        const x = cx + Math.cos(angle) * r;
        const y = cy + Math.sin(angle) * r;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.lineWidth = lineWidth;
      ctx.strokeStyle = baseColor;
      if (dashed) ctx.setLineDash([5, 4, 2, 3]);
      ctx.stroke();

      // secondary sketchy overlay
      ctx.beginPath();
      for (let i = 0; i <= steps; i++) {
        const angle = (i / steps) * Math.PI * 2;
        let noise2 = Math.sin(angle * 5 + time * 4.2) * 0.8 + Math.cos(angle * 8.3 + time * 3.1) * 0.6;
        const r = radius + noise2 * (wobbleFactor * 0.8);
        const x = cx + Math.cos(angle) * r;
        const y = cy + Math.sin(angle) * r;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.strokeStyle = baseColor + "b0";
      ctx.lineWidth = lineWidth * 0.7;
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }

    // electric wire / coil with active pulses (warm orange-copper)
    function electricLoop(cx, cy, radius, phase, colorCore = "#f97316") {
      ctx.save();
      ctx.shadowBlur = 6;
      ctx.shadowColor = "#fb923c";
      for (let layer = 0; layer < 2; layer++) {
        ctx.beginPath();
        const steps = 64;
        for (let i = 0; i <= steps; i++) {
          const tAngle = (i / steps) * Math.PI * 2;
          const ripple = Math.sin(tAngle * 4 + time * 7) * 2.5 + Math.sin(tAngle * 9 - time * 5.5) * 1.6;
          const rMod = radius + ripple * 1.4 + Math.sin(time * 2.3) * 1.2;
          const x = cx + Math.cos(tAngle + phase) * rMod;
          const y = cy + Math.sin(tAngle) * rMod * 0.96;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.lineWidth = 2.3 + Math.sin(time * 11) * 0.5;
        ctx.strokeStyle = layer === 0 ? colorCore : `rgba(249, 115, 22, 0.75)`;
        ctx.stroke();
      }

      // add glow nodes on the loop
      for (let k = 0; k < 14; k++) {
        const angleNode = (k / 14) * Math.PI * 2 + time * 2.7;
        const radNode = radius + Math.sin(angleNode * 3 + time * 9) * 2.2;
        const xNode = cx + Math.cos(angleNode) * radNode;
        const yNode = cy + Math.sin(angleNode) * radNode * 0.96;
        const pulse = (Math.sin(time * 14 + k) * 0.5 + 0.6);
        if (pulse > 0.68) {
          ctx.beginPath();
          ctx.arc(xNode, yNode, 2.5 + Math.sin(time * 21 + k) * 1.2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(251, 146, 60, ${0.7 + pulse * 0.3})`;
          ctx.fill();
          ctx.beginPath();
          ctx.moveTo(xNode - 4, yNode);
          ctx.lineTo(xNode + 4, yNode);
          ctx.moveTo(xNode, yNode - 4);
          ctx.lineTo(xNode, yNode + 4);
          ctx.lineWidth = 1.5;
          ctx.strokeStyle = "#ffedd5";
          ctx.stroke();
        }
      }
      ctx.restore();
    }

    // central lightning bolt with energetic skin-friendly orange glow
    function fireBolt(cx, cy, boltSize, intensityFlash = 1.0) {
      ctx.save();
      ctx.shadowBlur = 12;
      ctx.shadowColor = "#f97316";
      const wiggleX = Math.sin(time * 14) * 1.2 + Math.cos(time * 9.3) * 0.9;
      const topY = cy - boltSize * 0.68;
      const mid1Y = cy - boltSize * 0.2;
      const mid2Y = cy + boltSize * 0.2;
      const bottomY = cy + boltSize * 0.72;

      let points = [
        { x: cx + wiggleX * 0.5, y: topY },
        { x: cx - boltSize * 0.24 + wiggleX * 1.1, y: mid1Y - boltSize * 0.1 },
        { x: cx + boltSize * 0.3 + wiggleX * 0.7, y: mid1Y },
        { x: cx - boltSize * 0.19 + wiggleX * 1.5, y: mid2Y + boltSize * 0.05 },
        { x: cx + boltSize * 0.28, y: mid2Y + boltSize * 0.02 },
        { x: cx + wiggleX * 0.4, y: bottomY }
      ];

      for (let i = 0; i < points.length; i++) {
        points[i].x += Math.sin(time * 11 + i * 1.9) * 1.3 * intensityFlash;
        points[i].y += Math.cos(time * 8.7 + i * 2.4) * 1.1 * intensityFlash;
      }

      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
      ctx.lineWidth = 3.5 + Math.sin(time * 22) * 1.2;
      ctx.strokeStyle = `rgba(249, 115, 22, ${0.9 + Math.sin(time * 18) * 0.1})`;
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#fff7ed";
      ctx.stroke();

      for (let s = 0; s < 8; s++) {
        let sparkAng = (Date.now() * 0.009 + s) % 1;
        if (sparkAng > 0.65) {
          let angRand = Math.random() * Math.PI * 2;
          let radSp = boltSize * 0.55;
          let sx = cx + Math.cos(angRand + time * 3) * radSp * 0.8;
          let sy = cy + Math.sin(angRand * 1.4 + time) * radSp * 0.6 - 6;
          ctx.beginPath();
          ctx.arc(sx, sy, 2.2 + Math.sin(time * 26 + s) * 1.2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(251, 146, 60, 0.9)`;
          ctx.fill();
        }
      }
      ctx.restore();
    }

    // orbiting energetic particles (copper / gold)
    function orbitCharges(cx, cy, radius, count, colorGlow = "#f97316") {
      for (let i = 0; i < count; i++) {
        const angle = (i / count) * Math.PI * 2 + time * 5.2;
        const radiusVar = radius + Math.sin(time * 3.5 + i) * 2.5;
        const x = cx + Math.cos(angle) * radiusVar;
        const y = cy + Math.sin(angle) * radiusVar * 0.94;
        const intensity = Math.sin(time * 13 + i) * 0.6 + 0.6;
        ctx.beginPath();
        ctx.arc(x, y, 2.8 + Math.sin(time * 24 + i) * 1.1, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(249, 115, 22, ${0.85})`;
        ctx.shadowBlur = 8;
        ctx.shadowColor = "#fb923c";
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x - 1, y - 1, 1.2, 0, Math.PI * 2);
        ctx.fillStyle = "#fff7ed";
        ctx.fill();
      }
    }

    // spark trails (organic squiggly arcs)
    function sparkTrails(cx, cy, radius) {
      ctx.save();
      ctx.setLineDash([2, 5, 1, 4]);
      for (let s = 0; s < 10; s++) {
        const startA = (s / 10) * Math.PI * 2 + time * 1.6;
        const endA = startA + 0.85 + Math.sin(time * 2.2) * 0.4;
        const radVar = radius + 9 + Math.sin(time * 5.4 + s) * 3;
        const x1 = cx + Math.cos(startA) * radVar;
        const y1 = cy + Math.sin(startA) * radVar * 0.95;
        const x2 = cx + Math.cos(endA) * (radVar + 5);
        const y2 = cy + Math.sin(endA) * (radVar + 5) * 0.95;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.quadraticCurveTo(cx + Math.cos(startA + 0.45) * (radVar + 3), cy + Math.sin(startA + 0.5) * (radVar + 2), x2, y2);
        ctx.lineWidth = 1.6;
        ctx.strokeStyle = `rgba(249, 115, 22, 0.7)`;
        ctx.stroke();
      }
      ctx.setLineDash([]);
      ctx.restore();
    }

    // glowing doodle nodes (skin-friendly warm freckles)
    function glowDots(cx, cy, radius) {
      for (let d = 0; d < 28; d++) {
        const angleRand = d * 0.98 + time * 3.2;
        const radDot = radius + Math.sin(angleRand * 2 + time) * 8;
        const xD = cx + Math.cos(angleRand) * radDot;
        const yD = cy + Math.sin(angleRand * 0.95) * radDot * 0.92;
        const flick = Math.sin(time * 18 + d) * 0.5 + 0.5;
        if (flick > 0.55) {
          ctx.beginPath();
          ctx.arc(xD, yD, 1.6 + flick * 1.2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(251, 146, 60, ${0.55 + flick * 0.4})`;
          ctx.fill();
        }
      }
    }

    // background with soft skin-tone paper texture + doodle warmth
    function warmSkinBg() {
      // Use project bg-light or a very light orange tint
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);

      // hand-drawn messy grid (subtle)
      for (let i = 0; i < 200; i++) {
        ctx.fillStyle = `rgba(249, 115, 22, 0.02)`;
        ctx.beginPath();
        ctx.moveTo(i * 7, 0);
        ctx.lineTo(i * 7 + 4, size);
        ctx.lineTo(i * 7 - 4, size);
        ctx.fill();
      }
      for (let i = 0; i < 60; i++) {
        ctx.beginPath();
        ctx.moveTo(i * 18, 0);
        ctx.lineTo(i * 18 + 12, size);
        ctx.lineWidth = 0.6;
        ctx.strokeStyle = "#fb923c15";
        ctx.stroke();
      }
      // organic skin flecks
      for (let f = 0; f < 180; f++) {
        ctx.beginPath();
        ctx.arc(Math.random() * size, Math.random() * size, 0.8, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(249, 115, 22, 0.03)`;
        ctx.fill();
      }
    }

    // voltage ripples with soft energetic touch
    function voltageRipples(cx, cy, maxR) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      for (let w = 0; w < 3; w++) {
        ctx.beginPath();
        for (let a = 0; a <= Math.PI * 2; a += 0.2) {
          const radiusW = maxR + 18 + Math.sin(a * 4 + time * 8) * 4;
          const xW = cx + Math.cos(a + time * 1.2) * radiusW;
          const yW = cy + Math.sin(a * 1.1 + time * 1.8) * radiusW * 0.9;
          if (a === 0) ctx.moveTo(xW, yW);
          else ctx.lineTo(xW, yW);
        }
        ctx.closePath();
        ctx.strokeStyle = `rgba(249, 115, 22, 0.3)`;
        ctx.lineWidth = 1.4;
        ctx.stroke();
      }
      ctx.restore();
    }

    // main render — combines all doodle layers
    function renderElectricalDoodle() {
      if (!ctx) return;
      ctx.clearRect(0, 0, size, size);
      // warmSkinBg();

      const cx = size / 2;
      const cy = size / 2;
      const baseR = 118;

      // Outer and inner electric coils (earthy copper)
      electricLoop(cx, cy, baseR + 15, time * 0.9, "#f97316");
      electricLoop(cx, cy, baseR - 14, -time * 0.6, "#fb923c");

      // Sketchy concentric circles (hand-drawn wire insulation)
      sketchyCircle(cx, cy, baseR, 2.3, "#f97316", 1.7);
      sketchyCircle(cx, cy, baseR - 22, 2.0, "#fb923c", 1.45, true);
      sketchyCircle(cx, cy, baseR - 42, 1.9, "#fdba74", 1.3);

      // floating charges / active electrons
      orbitCharges(cx, cy, baseR - 10, 8, "#f97316");
      orbitCharges(cx, cy, baseR + 12, 7, "#fb923c");

      // center lightning bolt (theme core)
      const boltIntensity = 0.75 + Math.sin(time * 14) * 0.3;
      fireBolt(cx, cy - 4, 68, boltIntensity);

      // spark trails and ripples
      sparkTrails(cx, cy, baseR + 4);
      voltageRipples(cx, cy, baseR);

      // Nodes and glowing dots all around (like warm embers)
      glowDots(cx, cy, baseR + 8);
      glowDots(cx, cy, baseR - 24);

      // intricate cross spark nodes (electrical doodle accents)
      for (let n = 0; n < 14; n++) {
        const angNode = (n / 14) * Math.PI * 2 + time * 2.9;
        const radN = baseR - 4 + Math.sin(time * 5 + n) * 3.5;
        const xn = cx + Math.cos(angNode) * radN;
        const yn = cy + Math.sin(angNode) * radN * 0.96;
        ctx.beginPath();
        ctx.arc(xn, yn, 2.2 + Math.sin(time * 19 + n) * 1, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(249, 115, 22, 0.95)`;
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(xn - 4.5, yn);
        ctx.lineTo(xn + 4.5, yn);
        ctx.moveTo(xn, yn - 4.5);
        ctx.lineTo(xn, yn + 4.5);
        ctx.lineWidth = 1.6;
        ctx.strokeStyle = "#fdba74";
        ctx.stroke();
      }

      // loading text with doodle vibe
      ctx.font = `bold ${19 + Math.sin(time * 5) * 1.8}px 'Outfit', sans-serif`;
      ctx.shadowBlur = 0;
      // let loadMsg = `⚡ ${message} ⚡`;
      let loadMsg = `${message}`
      let msgX = cx - ctx.measureText(loadMsg).width / 2;
      let msgY = cy + baseR + 40;
      ctx.fillStyle = "#1e293b"; // Primary dark text
      ctx.fillText(loadMsg, msgX + 1, msgY + 1);
      ctx.fillStyle = "#f97316"; // Primary orange
      ctx.fillText(loadMsg, msgX, msgY);

      // floating ember particles (skin-warmth effect)
      for (let p = 0; p < 30; p++) {
        const randAng = p * 21 + time * 7.4;
        const radParticle = baseR + 13 + Math.sin(randAng) * 12;
        const xP = cx + Math.cos(randAng + time * 2.1) * radParticle;
        const yP = cy + Math.sin(randAng * 0.9 + time * 1.4) * radParticle * 0.88;
        const sizeP = 1.3 + Math.sin(time * 27 + p) * 1.1;
        ctx.beginPath();
        ctx.arc(xP, yP, sizeP, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(249, 115, 22, ${0.6 + Math.sin(time * 20 + p) * 0.3})`;
        ctx.fill();
      }

      // extra hand-drawn zigzag border (doodle frame)
      ctx.beginPath();
      ctx.setLineDash([7, 5, 3, 4]);
      for (let a = 0; a < Math.PI * 2; a += 0.25) {
        const rBord = baseR + 38 + Math.sin(a * 6 + time * 7) * 3.2;
        const xB = cx + Math.cos(a + time * 0.8) * rBord;
        const yB = cy + Math.sin(a + time * 0.5) * rBord * 0.9;
        if (a === 0) ctx.moveTo(xB, yB);
        else ctx.lineTo(xB, yB);
      }
      ctx.closePath();
      ctx.strokeStyle = "#f9731655";
      ctx.lineWidth = 1.4;
      ctx.stroke();
      ctx.setLineDash([]);
    }

    function animateLoader() {
      time = (performance.now() / 1000) * 1.35;
      renderElectricalDoodle();
      animationId = requestAnimationFrame(animateLoader);
    }

    animateLoader();

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [message]);

  return (
    <div className={styles.overlay}>
      <div className={styles.container}>
        <div className={styles.loaderWrapper}>
          <canvas ref={canvasRef} className={styles.canvas}></canvas>
        </div>
        {/* <div className={styles.caption}>
          ⚡ <span className={styles.message}>{message} • warm surge</span> ⚡
        </div> */}
        {/* <div className={styles.caption}> */}
        <span className={styles.message}>{message}</span>
        {/* </div> */}
        {/* <footer className={styles.footer}>✍️ project glow | hand‑drawn sparks ✍️</footer> */}
      </div>
    </div>
  );
};

export default DoodleLoader;
